import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { User } from 'oidc-client';
import { AuthService } from 'src/app/authentication/auth.service';
import { AppSettingsService } from 'src/app/services/common/appsettings.service';
import { ConfigurationSettingsEnum, ConstantService, ThemeEnum, UserRoles } from 'src/app/services/common/constant.service';
import { OrganizationModel, OrganizationService } from 'src/app/services/organization';
import { NgbModal, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu } from '@ng-bootstrap/ng-bootstrap';
import { AdminValidationModal } from 'src/app/user/admin-validation-modal/admin-validation-modal';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { UsersService } from 'src/app/services/user';
import { TenantService } from './tenant.service';

import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, ReactiveFormsModule, NgxIntlTelInputModule, RouterLink, TranslatePipe]
})

export class HeaderComponent implements OnInit {
  appSettings = inject(AppSettingsService);
  private constantService = inject(ConstantService);
  private authService = inject(AuthService);
  private organizationService = inject(OrganizationService);
  router = inject(Router);
  private modalService = inject(NgbModal);
  private fb = inject(FormBuilder);
  private userService = inject(UsersService);


  private tenantService = inject(TenantService);

  public activeLang = localStorage.getItem('locale') ?? 'en';
  showAdmin = this.appSettings.settings.adminMenu;

  user: User;
  organizations: any;
  filteredOrganizations: any;
  pagedOrg: any;
  pageSize = 5;
  currentIndex = 1;
  totalPages = 1;

  languages = [
    { id: 'en', name: 'English', active: false },
    { id: 'es', name: 'Español', active: false },
    { id: 'fr', name: 'Français', active: false },
    { id: 'de', name: 'Deutsch', active: false },
    { id: 'it', name: 'Italiano', active: false },
  ];

  profileImageUrl: string;

  private adminValidationEnabled = false;

  tenantForm: FormGroup;

  async ngOnInit() {
    this.profileImageUrl = "assets/images/user.png";
    this.user = await this.authService.getUser();

    if (this.canManageOtherTenants()) {

      this.tenantService.currentTenant.set(this.constantService.mspTenant);

      try{
        const data = await firstValueFrom(this.organizationService.apiOrganizationGetOrganizationAllGet());
        this.organizations = data?.sort((a, b) => a.organizationName?.localeCompare(b.organizationName));
        this.tenantService.defaultTenant.set(data.find(f => f.mnemonic == this.constantService.getDefaultTenant()));
        this.filteredOrganizations = this.organizations;
        this.setTenants();
      }catch(error){
        console.log(error);
      }
    }

    if(this.authService.userType != 'User'){
      this.getAdminValidationEnabled();
    }

    this.tenantForm = this.fb.group({
      search: ['']
    });

    this.tenantForm.controls.search.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(val => {
      val = val.toLowerCase();
      this.filteredOrganizations = this.organizations.filter(x => x.organizationName.toLowerCase().includes(val));
      this.currentIndex = 1;
      this.setTenants();
    })
  }

  setTenants() {
    this.totalPages = Math.ceil(this.filteredOrganizations.length / this.pageSize);
    var trimLeft: any = (this.currentIndex - 1) * this.pageSize;
    var trimRight: any = this.currentIndex * this.pageSize;
    (trimRight < this.filteredOrganizations[this.filteredOrganizations.length - 1]) ? this.currentIndex * this.pageSize : this.filteredOrganizations[(this.filteredOrganizations.length - 1)];
    this.pagedOrg = this.filteredOrganizations.slice(trimLeft, trimRight);
  }

  prevTenants() {
    this.currentIndex--;
    if (this.currentIndex < 1) this.currentIndex = 1;

    this.setTenants();
  }

  nextTenants() {
    this.currentIndex++;
    if (this.currentIndex > this.totalPages) {
      this.currentIndex--;
    }
    this.setTenants();
  }

  closeMenu(e) {
    e.target.closest('.dropdown').classList.remove('show');
    e.target.closest('.dropdown .dropdown-menu').classList.remove('show');
  }

  toggleSidebar(event) {
    event.preventDefault();
    this.appSettings.settings.showLeftMenu =
      !this.appSettings.settings.showLeftMenu;
    if (window.matchMedia('(min-width: 992px)').matches) {
      document.querySelector('body')!.classList.toggle('az-sidebar-hide');
    } else {
      document.querySelector('body')!.classList.toggle('az-sidebar-show');
    }
  }

  public isUser() {
    const userType = this.authService.userType;
    return (userType && userType.toLowerCase() === UserRoles.User.toLowerCase());
  }

  public canManageOtherTenants() {
    const userType = this.authService.userType;
    return (userType && (userType.toLowerCase() === UserRoles.SuperAdmin.toLowerCase() || userType.toLowerCase() == UserRoles.MspAdmin));
  }

  async setLanguage(lang, forceReload = false) {
    this.activeLang = lang.id;
    localStorage.setItem('locale', lang.id);
    await this.authService.loadLanguages(forceReload);

  }

  private getAdminValidationEnabled() {
    this.userService.apiUsersCheckAdminValidationStatusOrganizationIdUserIdPut(this.authService.user.profile.OrganizationId,  this.authService.user.profile.UserID).subscribe((data) => {
      if (data) {
        this.adminValidationEnabled = true;
      } else {
        this.adminValidationEnabled = false;
      }
    });
  }

  async setMenu() {
    this.getAdminValidationEnabled();

    if(this.adminValidationEnabled && !this.appSettings.settings.adminMenu){
      const modalRef = this.modalService.open(AdminValidationModal, { size: 'md' });
      modalRef.result.then(async (isAdminValidated) => {
        if (isAdminValidated) {
          await this.toggleMenu();
        }
      }).catch((error) => {
          console.error('Error in admin validation modal:', error);
      });
    }else{
      await this.toggleMenu();
    }
  }

  async toggleMenu(){
    this.appSettings.settings.adminMenu = !this.appSettings.settings.adminMenu;
    
    this.setLanguage(this.languages[0], true);
    
    if (this.appSettings.settings.adminMenu) {
        this.router.navigateByUrl('/dashboard');
    } else {
        this.router.navigateByUrl('/myapplications');
    }
  }

  public async signout() {
    await this.authService.signOut();
  }

  debounce = (func, timeout) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => { func.apply(this, args); }, timeout);
    }
  }
}
